/* REACT */
import { useState, useEffect } from "react";

/* ROUTER */
import { Link, useLocation } from "react-router-dom";

/* PLUGINS */
import {
  BarChart3,
  FileText,
  ClipboardCheck,
  Settings,
  Users,
  ChevronsLeft,
  Send,
  Pin,
} from "lucide-react";

/* ASSETS */
import idlLogo from "@/assets/idl-logo-header.png";

/* CONTEXTS */
import { useUnsavedChangesContext } from "@/contexts/UnsavedChangesContext";

const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed";
const SIDEBAR_PINNED_KEY = "sidebar-pinned";
/* Auto-collapse threshold — covers tablets and small laptops so wide tables (e.g. Attendance) can breathe. */
const NARROW_BREAKPOINT = 1024;

export const Sidebar = () => {
  const location = useLocation();
  const [pinned, setPinned] = useState(() => localStorage.getItem(SIDEBAR_PINNED_KEY) === "true");
  const [collapsed, setCollapsed] = useState(() => {
    const isPinned = localStorage.getItem(SIDEBAR_PINNED_KEY) === "true";
    const isNarrowScreen = window.innerWidth < NARROW_BREAKPOINT;
    /* Pinned sidebars always respect the saved preference, even on narrow screens. */
    if (isNarrowScreen && !isPinned) return true;

    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (saved !== null) return saved === "true";

    return false;
  });
  const { hasUnsavedChanges, setPendingNavigation } =
    useUnsavedChangesContext();

  // Persist collapsed state to localStorage and notify other components
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    window.dispatchEvent(
      new CustomEvent("sidebar-toggle", { detail: { collapsed } }),
    );
  }, [collapsed]);

  // Persist pinned preference
  useEffect(() => {
    localStorage.setItem(SIDEBAR_PINNED_KEY, String(pinned));
  }, [pinned]);

  // Auto-collapse sidebar on narrow (tablet / small-laptop) viewports — skipped when the user pinned it.
  useEffect(() => {
    const handleResize = () => {
      const isNarrowScreen = window.innerWidth < NARROW_BREAKPOINT;

      if (isNarrowScreen && !pinned) {
        setCollapsed(true);
        return;
      }

      const savedPreference = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (savedPreference !== null) {
        setCollapsed(savedPreference === "true");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [pinned]);

  const handleNavClick = (e: React.MouseEvent, path: string) => {
    if (hasUnsavedChanges && location.pathname !== path) {
      e.preventDefault();
      setPendingNavigation(path);
    }
  };

  const baseNavItems = [
    { icon: BarChart3, label: "Dashboard", path: "/dashboard" },
    { icon: FileText, label: "Forms", path: "/forms" },
    { icon: Send, label: "Reports", path: "/reports" },
    { icon: ClipboardCheck, label: "Attendance", path: "/attendance" },
    { icon: Users, label: "Crews", path: "/crews" },
  ];

  const navItems = [
    ...baseNavItems,
    { icon: Settings, label: "Settings", path: "/settings" },
  ];

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-sidebar border-r border-border shadow-sm z-50 overflow-hidden transition-all duration-300 ease-in-out ${
        collapsed ? "w-16" : "w-56"
      }`}
      role="navigation"
      aria-label="Sidebar navigation"
    >
      <div className="flex flex-col h-full">
        {/* Logo header — aligns to top bar height, logo indent matches nav-item icon column (22px) */}
        <div
          className={`flex items-center h-16 border-b border-border ${
            collapsed ? "justify-center px-2" : "justify-between pl-[22px] pr-2 gap-1"
          }`}
        >
          {!collapsed && (
            <Link
              to="/dashboard"
              onClick={(e) => handleNavClick(e, "/dashboard")}
              className="flex items-center overflow-hidden flex-1 min-w-0"
              aria-label="Go to dashboard"
            >
              <img
                src={idlLogo}
                alt="IDL Projects"
                className="h-10 w-auto max-w-none"
              />
            </Link>
          )}
          <div className="flex-shrink-0 flex items-center">
            {!collapsed && (
              <button
                onClick={() => setPinned((prev) => !prev)}
                className="flex items-center justify-center h-7 w-7 rounded-md text-brand hover:bg-brand/10 transition-colors"
                aria-label={pinned ? "Unpin sidebar" : "Pin sidebar"}
                aria-pressed={pinned}
                title={pinned ? "Sidebar pinned" : "Pin sidebar"}
              >
                <Pin
                  className={`w-4 h-4 transition-transform duration-200 ${pinned ? "rotate-45 fill-current" : ""}`}
                  aria-hidden="true"
                />
              </button>
            )}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="flex items-center justify-center h-7 w-7 rounded-md text-brand hover:bg-brand/10 transition-colors"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
            >
              <ChevronsLeft
                className={`w-4 h-4 transition-transform duration-300 ease-in-out ${collapsed ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 py-4">
          {navItems.map((item) => {
            const is_active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(e, item.path)}
                className={`relative flex items-center py-3 mx-2 rounded-lg transition-all ${
                  is_active
                    ? "bg-brand text-brand-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
                title={collapsed ? item.label : undefined}
                aria-current={is_active ? "page" : undefined}
              >
                <item.icon
                  className="w-5 h-5 flex-shrink-0 absolute left-[14px]"
                  aria-hidden="true"
                />
                <span
                  className={`font-medium text-sm whitespace-nowrap pl-[50px] transition-all duration-150 ease-in-out ${
                    collapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100 w-auto pr-4 [transition-delay:200ms]"
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};
